import { Options, documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, MARKS } from '@contentful/rich-text-types';
import React from 'react';
import { Button } from '../button/button';
import styles from './rich-text-viewer.module.scss';

interface RichTextViewerProps {
  content?: Document;
  listStyle?: 'default' | 'checkboxes';
  dark?: boolean;
}

// Component configuration interface
interface ComponentConfig {
  type: string;
  id?: string;
  className?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

// Utility function to check if a string is valid JSON
const isValidJSON = (str: string): boolean => {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

// Component renderer based on JSON configuration
const renderJSXComponent = (config: ComponentConfig): React.ReactNode => {
  const { type, children, className, ...props } = config;

  switch (type) {
    case 'button':
      return (
        <Button
          className={className}
          key={config.id}
          style={{
            paddingTop: '8px',
            paddingBottom: '8px',
          }}
          {...props}
        >
          {children}
        </Button>
      );
    default:
      // If component type is not recognized, render as a span with the configuration
      // Using span instead of div to avoid HTML validation issues when rendered inside paragraphs
      return (
        <span key={config.id} {...props}>
          {children}
        </span>
      );
  }
};

// Utility function to normalize text content: replace nbsp with normal spaces and convert \n to <br /> elements
const normalizeTextContent = (children: React.ReactNode): React.ReactNode => {
  if (typeof children === 'string') {
    // Replace non-breaking spaces with normal spaces
    const processedString = children.replace(/\u00A0/g, ' ');

    // Split on \n and create br elements
    const parts = processedString.split('\n');
    if (parts.length === 1) {
      return processedString; // No newlines, return the processed string
    }

    return parts.reduce((acc, part, index) => {
      if (index > 0) {
        acc.push(<br key={`br-${index}`} />);
      }
      if (part) {
        acc.push(part);
      }
      return acc;
    }, [] as React.ReactNode[]);
  }

  if (React.isValidElement(children)) {
    return children; // Return React elements as-is
  }

  if (Array.isArray(children)) {
    return children.map((child) =>
      typeof child === 'string' ? normalizeTextContent(child) : child,
    );
  }

  return children;
};

const options: Options = {
  renderMark: {
    [MARKS.BOLD]: (text: React.ReactNode) => (
      <strong className={styles.component_bold}>{text}</strong>
    ),
    [MARKS.ITALIC]: (text: React.ReactNode) => <em className={styles.component_italic}>{text}</em>,
    [MARKS.UNDERLINE]: (text: React.ReactNode) => (
      <u className={styles.component_underline}>{text}</u>
    ),
    [MARKS.CODE]: (text: React.ReactNode) => {
      if (typeof text === 'string') {
        const trimmedText = text.trim();

        // Check if the code content is valid JSON
        if (isValidJSON(trimmedText)) {
          try {
            const config = JSON.parse(trimmedText) as ComponentConfig;

            // If it's a valid component configuration, render the JSX component
            if (config.type) {
              return renderJSXComponent(config);
            }
          } catch {
            // If parsing fails, fall back to regular code rendering
          }
        }
      }

      // Default code rendering for non-JSON content
      return <code className={styles.component_code}>{text}</code>;
    },
    [MARKS.SUPERSCRIPT]: (text: React.ReactNode) => (
      <sup className={styles.component_superscript}>{text}</sup>
    ),
    [MARKS.SUBSCRIPT]: (text: React.ReactNode) => (
      <sub className={styles.component_subscript}>{text}</sub>
    ),
    [MARKS.STRIKETHROUGH]: (text: React.ReactNode) => (
      <s className={styles.component_strikethrough}>{text}</s>
    ),
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children: React.ReactNode) => {
      const processedChildren = children != '' ? normalizeTextContent(children) : null;

      return processedChildren ? (
        <p className={`${styles.component_paragraph} rich-text-paragraph`}>{processedChildren}</p>
      ) : null;
    },
    [BLOCKS.HEADING_1]: (node, children: React.ReactNode) => (
      <h1 className={styles.component_heading1}>{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (node, children: React.ReactNode) => (
      <h2 className={styles.component_heading2}>{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (node, children: React.ReactNode) => (
      <h3 className={styles.component_heading3}>{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (node, children: React.ReactNode) => (
      <h4 className={styles.component_heading4}>{children}</h4>
    ),
    [BLOCKS.HEADING_5]: (node, children: React.ReactNode) => (
      <h5 className={styles.component_heading5}>{children}</h5>
    ),
    [BLOCKS.HEADING_6]: (node, children: React.ReactNode) => (
      <h6 className={styles.component_heading6}>{children}</h6>
    ),
    [BLOCKS.HR]: () => <hr className={styles.component_horizontalRule} />,
    [BLOCKS.UL_LIST]: (node, children: React.ReactNode) => (
      <ul className={styles.component_unorderedList}>{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (node, children: React.ReactNode) => (
      <ol className={styles.component_orderedList}>{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (node, children: React.ReactNode) => (
      <li className={styles.component_listItem}>{children}</li>
    ),
    [BLOCKS.QUOTE]: (node, children: React.ReactNode) => (
      <blockquote className={styles.component_blockquote}>{children}</blockquote>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node) => (
      <div className={styles.component_embeddedEntry}>
        {/* Custom rendering logic for embedded entries goes here */}
        <p>Embedded Entry: {node.data.target?.sys?.id || 'Unknown ID'}</p>
      </div>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (node) => (
      <div className={styles.component_embeddedAsset}>
        {/* Custom rendering logic for embedded assets goes here */}
        <p>Embedded Asset: {node.data.target?.sys?.id || 'Unknown ID'}</p>
      </div>
    ),
    [BLOCKS.EMBEDDED_RESOURCE]: (node) => (
      <div className={styles.component_embeddedResource}>
        {/* Custom rendering logic for embedded resources goes here */}
        <p>Embedded Resource: {node.data.target?.sys?.urn || 'Unknown URN'}</p>
      </div>
    ),
    [BLOCKS.TABLE]: (node, children: React.ReactNode) => (
      <div className={styles.component_tableWrapper}>
        <table className={styles.component_table}>
          <tbody>{children}</tbody>
        </table>
      </div>
    ),
    [BLOCKS.TABLE_ROW]: (node, children: React.ReactNode) => (
      <tr className={styles.component_tableRow}>{children}</tr>
    ),
    [BLOCKS.TABLE_CELL]: (node, children: React.ReactNode) => (
      <td className={styles.component_tableCell}>{children}</td>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (node, children: React.ReactNode) => (
      <th className={styles.component_tableHeaderCell}>{children}</th>
    ),
    [INLINES.HYPERLINK]: (node, children: React.ReactNode) => {
      const uri = node.data.uri;
      const isExternal = uri?.startsWith('http://') || uri?.startsWith('https://');
      const isAnchor = uri?.startsWith('#');
      const openInNew = isExternal && !isAnchor;
      return (
        <a
          href={uri}
          target={openInNew ? '_blank' : '_self'}
          rel={openInNew ? 'noopener noreferrer' : undefined}
          className={styles.component_hyperlink}
        >
          {children}
        </a>
      );
    },
    [INLINES.ENTRY_HYPERLINK]: (node, children: React.ReactNode) => (
      <a href={`#entry-${node.data.target?.sys?.id}`} className={styles.component_entryHyperlink}>
        {children}
      </a>
    ),
    [INLINES.ASSET_HYPERLINK]: (node, children: React.ReactNode) => (
      <a href={`#asset-${node.data.target?.sys?.id}`} className={styles.component_assetHyperlink}>
        {children}
      </a>
    ),
    [INLINES.RESOURCE_HYPERLINK]: (node, children: React.ReactNode) => (
      <a
        href={`#resource-${node.data.target?.sys?.urn}`}
        className={styles.component_resourceHyperlink}
      >
        {children}
      </a>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node) => (
      <span className={styles.component_embeddedEntryInline}>
        {/* Custom rendering logic for inline embedded entries goes here */}
        Embedded Entry (Inline): {node.data.target?.sys?.id || 'Unknown ID'}
      </span>
    ),
    [INLINES.EMBEDDED_RESOURCE]: (node) => (
      <span className={styles.component_embeddedResourceInline}>
        {/* Custom rendering logic for inline embedded resources goes here */}
        Embedded Resource (Inline): {node.data.target?.sys?.urn || 'Unknown URN'}
      </span>
    ),
  },
};

const checkboxOptions: Options = {
  ...options,
  renderNode: {
    ...options.renderNode,
    [BLOCKS.UL_LIST]: (node, children: React.ReactNode) => (
      <ul className={styles.component_checkboxList}>{children}</ul>
    ),
    [BLOCKS.LIST_ITEM]: (node, children: React.ReactNode) => (
      <li className={styles.component_checkboxItem}>
        <span className={styles.component_checkboxIcon}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        <span>{children}</span>
      </li>
    ),
  },
};

const RichTextViewer = ({ content, listStyle, dark }: RichTextViewerProps) => {
  if (!content) {
    return null;
  }

  const opts = listStyle === 'checkboxes' ? checkboxOptions : options;
  const className = dark ? `${styles.component} ${styles['component--dark']}` : styles.component;
  return <div className={className}>{documentToReactComponents(content, opts)}</div>;
};

export default RichTextViewer;
